import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useAgent } from '../../context/AgentContext';
import { Upload, File, X, CheckCircle, AlertCircle } from 'lucide-react';
import './DocumentUpload.css';

const DocumentUpload = () => {
  const { selectedAgent, uploadDocument, refreshDocuments } = useAgent();
  const [uploads, setUploads] = useState([]);

  const onDrop = useCallback(async (acceptedFiles) => {
    for (const file of acceptedFiles) {
      const uploadId = Date.now() + Math.random();

      setUploads(prev => [...prev, {
        id: uploadId,
        name: file.name,
        size: file.size,
        progress: 0,
        status: 'uploading'
      }]);

      try {
        await uploadDocument(file, selectedAgent?._id, (progress) => {
          setUploads(prev => prev.map(u =>
            u.id === uploadId ? { ...u, progress } : u
          ));
        });

        setUploads(prev => prev.map(u =>
          u.id === uploadId ? { ...u, status: 'success', progress: 100 } : u
        ));

        // Refresh documents list
        await refreshDocuments();

        // Remove successful upload after delay
        setTimeout(() => {
          setUploads(prev => prev.filter(u => u.id !== uploadId));
        }, 3000);

      } catch (error) {
        setUploads(prev => prev.map(u =>
          u.id === uploadId ? { ...u, status: 'error', error: error.message } : u
        ));
      }
    }
  }, [selectedAgent, uploadDocument, refreshDocuments]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  const removeUpload = (id) => {
    setUploads(prev => prev.filter(u => u.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="document-upload">
      <div
        {...getRootProps()}
        className={`upload-dropzone ${isDragActive ? 'active' : ''}`}
      >
        <input {...getInputProps()} />
        <div className="dropzone-content">
          <Upload size={32} className="upload-icon" />
          <h4>Upload Documents</h4>
          <p>
            {isDragActive
              ? 'Drop files here...'
              : 'Drag & drop files here, or click to browse'}
          </p>
          <span className="upload-hint">
            Supports PDF, DOCX, DOC, TXT (max 10MB)
          </span>
        </div>
      </div>

      {uploads.length > 0 && (
        <div className="upload-list">
          {uploads.map((upload) => (
            <div key={upload.id} className={`upload-item ${upload.status}`}>
              <div className="upload-item-icon">
                <File size={20} />
              </div>
              <div className="upload-item-info">
                <div className="upload-item-header">
                  <span className="upload-item-name">{upload.name}</span>
                  <span className="upload-item-size">{formatFileSize(upload.size)}</span>
                </div>
                {upload.status === 'uploading' && (
                  <div className="upload-progress">
                    <div
                      className="upload-progress-bar"
                      style={{ width: `${upload.progress}%` }}
                    />
                  </div>
                )}
                {upload.status === 'error' && (
                  <span className="upload-error">{upload.error}</span>
                )}
              </div>
              <div className="upload-item-status">
                {upload.status === 'uploading' && (
                  <span className="spinner" />
                )}
                {upload.status === 'success' && (
                  <CheckCircle size={20} className="status-success" />
                )}
                {upload.status === 'error' && (
                  <button
                    className="btn btn-icon btn-ghost"
                    onClick={() => removeUpload(upload.id)}
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
